import {
  DEFAULT_WALL_COLOR,
  DEFAULT_WALL_THICKNESS
} from "../components/map/defines";
import {
  Shape,
  WallPathEdge,
  WallPathShape,
  WallPathVertex,
  WallShape
} from "../types";
import { generateShapeId } from "./generateShapeId";

const MERGE_EPSILON = 1;
// Tolerance for chaining segments and for deciding a chain forms a
// closed loop. Legacy-SVG imports can land matching corners a few pixels
// apart (collinear-merge snapping) — with a 1px tolerance the last side
// of a loop never joins the chain and survives as a standalone stub wall
// instead of the path simply being closed.
const CLOSE_EPSILON = 6;

interface EndpointRef {
  wallIndex: number;
  endpoint: "start" | "end";
}

/**
 * Migrate legacy WallShape segments into WallPathShape chains.
 * Connected wall segments (endpoints within MERGE_EPSILON) are chained
 * into multi-vertex paths. Standalone walls become 2-vertex paths.
 */
export const migrateWallsToPath = (shapes: Shape[]): Shape[] => {
  const walls: WallShape[] = [];
  const nonWalls: Shape[] = [];

  for (const shape of shapes) {
    if (shape.type === "wall") {
      walls.push(shape as WallShape);
    } else {
      nonWalls.push(shape);
    }
  }

  if (walls.length === 0) return shapes;

  // Build adjacency: for each endpoint, find connected endpoints
  const getPoint = (wall: WallShape, ep: "start" | "end") =>
    ep === "start" ? { x: wall.x, y: wall.y } : { x: wall.x2, y: wall.y2 };

  const eps2 = CLOSE_EPSILON * CLOSE_EPSILON;
  const near = (a: { x: number; y: number }, b: { x: number; y: number }) => {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return dx * dx + dy * dy <= eps2;
  };

  // adjacency[wallIndex][endpoint] = list of connected (wallIndex, endpoint) pairs
  const adjacency: Map<string, EndpointRef[]> = new Map();
  const key = (wi: number, ep: "start" | "end") => `${wi}-${ep}`;

  for (let i = 0; i < walls.length; i++) {
    adjacency.set(key(i, "start"), []);
    adjacency.set(key(i, "end"), []);
  }

  for (let i = 0; i < walls.length; i++) {
    for (let j = i + 1; j < walls.length; j++) {
      const endpoints: Array<["start" | "end", "start" | "end"]> = [
        ["start", "start"],
        ["start", "end"],
        ["end", "start"],
        ["end", "end"]
      ];
      for (const [epI, epJ] of endpoints) {
        if (near(getPoint(walls[i], epI), getPoint(walls[j], epJ))) {
          adjacency.get(key(i, epI))!.push({ wallIndex: j, endpoint: epJ });
          adjacency.get(key(j, epJ))!.push({ wallIndex: i, endpoint: epI });
        }
      }
    }
  }

  // Chain walls into paths using DFS
  const visited = new Set<number>();
  const paths: WallPathShape[] = [];

  for (let startWall = 0; startWall < walls.length; startWall++) {
    if (visited.has(startWall)) continue;

    // Find chain start: prefer a wall with a free endpoint (degree < 1)
    // For simple chains, walk to one end first
    const chain: { wallIndex: number; reversed: boolean }[] = [];
    const buildChain = (wallIdx: number) => {
      // Find one end of the chain by following connections from start endpoint
      let currentWall = wallIdx;
      let currentEndpoint: "start" | "end" = "start";

      // Walk backward to find chain start
      const walkVisited = new Set<number>();
      walkVisited.add(currentWall);

      while (true) {
        const neighbors: EndpointRef[] = adjacency.get(
          key(currentWall, currentEndpoint)
        )!;
        const unvisitedNeighbor: EndpointRef | undefined = neighbors.find(
          (n: EndpointRef) =>
            !walkVisited.has(n.wallIndex) && !visited.has(n.wallIndex)
        );
        if (!unvisitedNeighbor) break;
        walkVisited.add(unvisitedNeighbor.wallIndex);
        currentWall = unvisitedNeighbor.wallIndex;
        // The neighbor's connected endpoint was matched; the "other" endpoint is the new current
        currentEndpoint =
          unvisitedNeighbor.endpoint === "start" ? "end" : "start";
      }

      // Now walk forward from currentWall, currentEndpoint (which is a free end)
      // currentEndpoint is the free end; so we start traversing from the other end
      let walkEndpoint: "start" | "end" = currentEndpoint;
      let walkWall = currentWall;
      const chainVisited = new Set<number>();

      while (true) {
        chainVisited.add(walkWall);
        visited.add(walkWall);
        // We entered this wall at walkEndpoint; the wall goes from walkEndpoint to otherEndpoint
        const reversed = walkEndpoint === "end";
        chain.push({ wallIndex: walkWall, reversed });

        // Move to the other endpoint of this wall
        const otherEndpoint: "start" | "end" =
          walkEndpoint === "start" ? "end" : "start";
        const neighbors = adjacency.get(key(walkWall, otherEndpoint))!;
        const next = neighbors.find(
          (n) => !chainVisited.has(n.wallIndex) && !visited.has(n.wallIndex)
        );
        if (!next) break;

        walkWall = next.wallIndex;
        walkEndpoint = next.endpoint;
      }
    };

    buildChain(startWall);

    // Convert chain to WallPathShape
    const vertices: WallPathVertex[] = [];
    const edges: WallPathEdge[] = [];
    const firstWall = walls[chain[0].wallIndex];
    const firstColor = firstWall.color || DEFAULT_WALL_COLOR;
    const firstThickness = firstWall.thickness || DEFAULT_WALL_THICKNESS;

    for (let ci = 0; ci < chain.length; ci++) {
      const { wallIndex, reversed } = chain[ci];
      const wall = walls[wallIndex];
      const startPt = reversed
        ? { x: wall.x2, y: wall.y2 }
        : { x: wall.x, y: wall.y };
      const endPt = reversed
        ? { x: wall.x, y: wall.y }
        : { x: wall.x2, y: wall.y2 };

      if (ci === 0) {
        vertices.push(startPt);
      }
      vertices.push(endPt);

      const edge: WallPathEdge = {};
      if (wall.curvature) {
        edge.curvature = reversed ? -wall.curvature : wall.curvature;
        edge.curvatureCenter = reversed
          ? 1 - (wall.curvatureCenter ?? 0.5)
          : wall.curvatureCenter;
      }
      if (wall.doors && wall.doors.length > 0) {
        edge.doors = reversed
          ? wall.doors.map((d) => ({ ...d, position: 1 - d.position }))
          : [...wall.doors];
      }
      if (wall.windows && wall.windows.length > 0) {
        edge.windows = reversed
          ? wall.windows.map((w) => ({ ...w, position: 1 - w.position }))
          : [...wall.windows];
      }
      edges.push(edge);
    }

    // Check if path is closed (last vertex matches first)
    const distance = (a: WallPathVertex, b: WallPathVertex) =>
      Math.hypot(a.x - b.x, a.y - b.y);
    // Drop trailing micro-stub segments that only exist to bridge the
    // loop back to the start — the closing edge replaces them.
    while (
      vertices.length > 3 &&
      distance(vertices[vertices.length - 2], vertices[vertices.length - 1]) <=
        CLOSE_EPSILON &&
      distance(vertices[0], vertices[vertices.length - 2]) <= CLOSE_EPSILON
    ) {
      vertices.pop();
      edges.pop();
    }
    let closed = false;
    if (vertices.length > 2) {
      const gap = distance(vertices[0], vertices[vertices.length - 1]);
      if (gap <= MERGE_EPSILON) {
        // Coincident endpoints — remove the duplicate closing vertex;
        // the existing last edge becomes the closing edge.
        vertices.pop();
        closed = true;
      } else if (gap <= CLOSE_EPSILON) {
        // Near-loop — keep the last real corner and let a new implicit
        // closing edge bridge the small gap.
        edges.push({});
        closed = true;
      }
    }

    const xs = vertices.map((v) => v.x);
    const ys = vertices.map((v) => v.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);

    const wallPath: WallPathShape = {
      id: generateShapeId("wall-path"),
      type: "wall-path",
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      vertices,
      edges,
      thickness: firstThickness,
      closed,
      color: firstColor,
      // Same caps the wall-path drawing tool uses — legacy segments have
      // no cap information, and butt ends look clipped at every corner.
      lineCapStart: "round",
      lineCapEnd: "round",
      layerType: "shapes"
    };

    paths.push(wallPath);
  }

  // Degenerate leftovers: chain walks can strand a few-pixel bridging
  // stub as its own 2-vertex path — shorter than a wall is thick, and
  // its loop already closed without it. Pure noise, drop it.
  const cleanedPaths = paths.filter((path) => {
    if (path.closed || path.vertices.length !== 2) return true;
    const [a, b] = path.vertices;
    return Math.hypot(a.x - b.x, a.y - b.y) > CLOSE_EPSILON;
  });

  return [...nonWalls, ...cleanedPaths];
};
